import type { AllowedGrades } from '@/src/app/hooks';
import type { ResourceHeaderDTO, ResourceItemDTO, UnitDTO } from '@/src/ui';
import {
    filterAssessmentsData,
    filterCentersData,
    filterLanguageSetting,
    filterResourcesData,
    filterSlidesData,
    filterUnitsData,
    getDisplayGrade,
    getTextbookLinks,
    type LanguageOption,
} from '@/src/utils';

import apiRequest from './axiosClient';

export const SEARCH_SOURCES = [
    'Lessons',
    'Assessments',
    'Resources',
    'BTBF',
    'Professional Learning',
    'User Guides',
    'eBooks',
] as const;

export type SearchSource = (typeof SEARCH_SOURCES)[number];

export interface SearchResult {
    /** Stable React key. Links repeat across grades, so the key folds in the origin. */
    key: string;
    /** The file name as it appears on the page, e.g. 'Homework'. */
    name: string;
    link: string;
    source: SearchSource;
    /** Undefined for grade-independent sources (BTBF, Professional Learning, User Guides). */
    grade?: string;
    /** Where the file lives, outermost first, e.g. ['Unit 3: Fractions', 'Lesson 5: Compare']. */
    trail: string[];
    /** The name alone, canonicalized -- ranks name matches above context-only ones. */
    nameKey: string;
    /**
     * Everything a teacher might type, canonicalized and precomputed: the name plus the
     * categories around it (grade, source, unit, lesson, box and submenu headings).
     * File names on their own are far too generic to search -- every lesson in every
     * grade has a file called 'Homework' -- so 'unit 1 homework' has to match on the
     * surrounding context, not the name alone.
     */
    haystack: string;
}

export interface SearchIndex {
    results: SearchResult[];
    /** Sources that failed to load, so the UI can say the index is incomplete. */
    failedSources: string[];
}

// How many API calls are in flight while building the index. Keeps the burst
// (3 calls per allowed grade, plus the shared sources) from stampeding the
// token refresh in axiosClient.
const REQUEST_CONCURRENCY = 6;

async function mapWithConcurrency<T>(
    items: T[],
    limit: number,
    task: (item: T) => Promise<void>
): Promise<void> {
    let cursor = 0;
    const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (cursor < items.length) {
            const item = items[cursor++];
            await task(item);
        }
    });
    await Promise.all(workers);
}

interface WalkContext {
    source: SearchSource;
    grade?: string;
    out: SearchResult[];
}

/**
 * Binds a number to the label in front of it: "grade 8" -> "grade8", "unit 1" -> "unit1".
 *
 * Without this a bare "8" matches anywhere, so "grade 8 unit 1 homework" happily matched
 * Grade 1's Unit 1 *Lesson 8* homework -- the 8 landed on the lesson number. Collapsing
 * both the query and the indexed text the same way makes "grade8" unable to match a
 * result that only ever says "grade1" and "lesson8".
 *
 * Only numbers are bound, so category headings like "Unit Tests" stay two words and
 * remain searchable as such.
 */
function canonicalize(text: string): string {
    return text
        .toLowerCase()
        .replace(/\bgrade\s+(tk|k|\d+m?)\b/g, 'grade$1')
        .replace(/\bunit\s+(\d+)/g, 'unit$1')
        .replace(/\blesson\s+(\d+)/g, 'lesson$1');
}

/**
 * '6M' displays as '6', so index both forms: a teacher searching "grade 6" sees the
 * accelerated files too (the UI labels them "Grade 6"), and "grade 6M" still narrows
 * to just them.
 */
function gradeTerms(grade: string | undefined): string {
    if (!grade) return '';
    const terms = new Set([`grade ${getDisplayGrade(grade)}`, `grade ${grade}`, `g${grade}`]);
    return [...terms].join(' ');
}

function push(ctx: WalkContext, name: string, link: string, trail: string[]) {
    if (!name || !link) return;
    ctx.out.push({
        key: `${ctx.source}|${ctx.grade ?? '-'}|${trail.join('>')}|${name}|${link}`,
        name,
        link,
        source: ctx.source,
        grade: ctx.grade,
        trail,
        nameKey: canonicalize(name),
        haystack: canonicalize(`${name} ${trail.join(' ')} ${ctx.source} ${gradeTerms(ctx.grade)}`),
    });
}

/**
 * Mirrors how ResourceBoxInner and RenderResourceSubmenu decide what is a clickable
 * leaf, so search never surfaces something a teacher cannot open from the page itself.
 * At depth 0 a box item only expands when it is flagged collapsible; deeper down any
 * item with subitems expands.
 */
function walkResourceItem(item: ResourceItemDTO, trail: string[], depth: number, ctx: WalkContext) {
    const hasSubitems = Boolean(item.subitems && item.subitems.length > 0);
    const expandable = depth === 0 ? Boolean(item.collapsible) && hasSubitems : hasSubitems;

    if (expandable) {
        const childTrail = item.text ? [...trail, item.text] : trail;
        item.subitems!.forEach((subitem) => walkResourceItem(subitem, childTrail, depth + 1, ctx));
        return;
    }

    if (item.link) {
        push(ctx, item.text, item.link, trail);
    }
}

function flattenResourceHeaders(
    headers: ResourceHeaderDTO[] | null | undefined,
    source: SearchSource,
    grade: string | undefined,
    out: SearchResult[]
) {
    if (!headers) return;
    const ctx: WalkContext = { source, grade, out };
    for (const box of headers) {
        for (const item of box.items || []) {
            walkResourceItem(item, box.title ? [box.title] : [], 0, ctx);
        }
    }
}

function flattenUnits(
    units: UnitDTO[] | null | undefined,
    grade: string,
    language: string,
    out: SearchResult[]
) {
    if (!units) return;
    const ctx: WalkContext = { source: 'Lessons', grade, out };

    for (const unit of units) {
        const unitLabel = `Unit ${unit.unitNumber}: ${unit.unitTitle}`;

        for (const lesson of unit.lessons || []) {
            const subitems = lesson.subitems || [];

            // Lesson 99 is the unit-level bucket (slides + centers) that CollapsibleUnit
            // renders above the lesson list, and it carries its own availability filters.
            if (lesson.lessonNumber === 99) {
                const centers = subitems.filter((item) => item.type === 'Centers');
                const slides = subitems.filter((item) => item.type !== 'Centers');
                const visible = [
                    ...filterSlidesData(grade, unit.unitNumber, slides, language),
                    ...filterCentersData(grade, unit.unitNumber, centers),
                ];
                visible.forEach((item) => push(ctx, item.text, item.link, [unitLabel]));
                continue;
            }

            const lessonLabel = `Lesson ${lesson.lessonNumber}: ${lesson.title}`;
            subitems.forEach((item) => push(ctx, item.text, item.link, [unitLabel, lessonLabel]));
        }
    }
}

function flattenTextbooks(grades: string[], language: LanguageOption, out: SearchResult[]) {
    for (const grade of grades) {
        const ctx: WalkContext = { source: 'eBooks', grade, out };
        const gradeLabel = `Grade ${getDisplayGrade(grade)}`;
        const { teacherEdition, studentEdition } = getTextbookLinks(grade, language);

        teacherEdition.forEach((link) =>
            push(ctx, `${gradeLabel} Teacher Edition ${link.displayText}`, link.link, [
                gradeLabel,
                'Teacher Edition',
            ])
        );
        studentEdition.forEach((link) =>
            push(ctx, `${gradeLabel} Student Edition ${link.displayText}`, link.link, [
                gradeLabel,
                'Student Edition',
            ])
        );
    }
}

export function getSearchableGrades(allowedGrades: AllowedGrades | undefined): string[] {
    if (!allowedGrades) return [];
    return [...(allowedGrades.elementary || []), ...(allowedGrades.middleSchool || [])];
}

/** Shape of `GET dbo/search` -- the whole index in one response. */
interface SearchIndexGradeResponse {
    grade: string;
    effectiveLanguage: string;
    units: UnitDTO[];
    assessments: ResourceHeaderDTO[];
    resources: ResourceHeaderDTO[];
}

interface SearchIndexResponse {
    language: string;
    grades: SearchIndexGradeResponse[];
    btbf: ResourceHeaderDTO[];
    professionalLearning: ResourceHeaderDTO[];
    userGuides: ResourceHeaderDTO[];
}

/**
 * One request for the whole index. The server decides which grades the caller may see
 * and hands back the same payload shapes the per-page endpoints serve, so the content
 * filters below stay exactly as they are.
 */
async function buildIndexFromBatch(language: LanguageOption): Promise<SearchIndex> {
    const results: SearchResult[] = [];

    const res = await apiRequest<SearchIndexResponse>('GET', `dbo/search?language=${language}`);
    const data = res.data;

    if (!data || !Array.isArray(data.grades)) {
        throw new MissingBatchEndpointError();
    }

    for (const block of data.grades) {
        const grade = block.grade;
        const effectiveLanguage = block.effectiveLanguage || filterLanguageSetting(grade, language);

        flattenUnits(filterUnitsData(grade, block.units), grade, effectiveLanguage, results);
        flattenResourceHeaders(
            filterAssessmentsData(block.assessments, grade, effectiveLanguage),
            'Assessments',
            grade,
            results
        );
        flattenResourceHeaders(
            filterResourcesData(block.resources, grade, effectiveLanguage),
            'Resources',
            grade,
            results
        );
    }

    flattenResourceHeaders(data.btbf, 'BTBF', undefined, results);
    flattenResourceHeaders(data.professionalLearning, 'Professional Learning', undefined, results);
    flattenResourceHeaders(data.userGuides, 'User Guides', undefined, results);

    flattenTextbooks(
        data.grades.map((block) => block.grade),
        language,
        results
    );

    return { results, failedSources: [] };
}

class MissingBatchEndpointError extends Error {
    constructor() {
        super('dbo/search did not return a search index');
    }
}

function isEndpointMissing(error: unknown): boolean {
    if (error instanceof MissingBatchEndpointError) return true;
    const status =
        (error as { response?: { status?: number } })?.response?.status ??
        (error as { status?: number })?.status;
    return status === 404;
}

async function buildIndex(grades: string[], language: LanguageOption): Promise<SearchIndex> {
    try {
        return await buildIndexFromBatch(language);
    } catch (error) {
        if (!isEndpointMissing(error)) throw error;
        return buildIndexFromPerGradeEndpoints(grades, language);
    }
}

async function buildIndexFromPerGradeEndpoints(
    grades: string[],
    language: LanguageOption
): Promise<SearchIndex> {
    const results: SearchResult[] = [];
    const failedSources: string[] = [];

    // Grade-independent sources. BTBF is the only one the API varies by language.
    const sharedTasks: { label: string; run: () => Promise<void> }[] = [
        {
            label: 'BTBF',
            run: async () => {
                const res = await apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    `dbo/btbf?language=${language}`
                );
                flattenResourceHeaders(res.data, 'BTBF', undefined, results);
            },
        },
        {
            label: 'Professional Learning',
            run: async () => {
                const res = await apiRequest<ResourceHeaderDTO[]>(
                    'GET',
                    'dbo/professionalLearning'
                );
                flattenResourceHeaders(res.data, 'Professional Learning', undefined, results);
            },
        },
        {
            label: 'User Guides',
            run: async () => {
                const res = await apiRequest<ResourceHeaderDTO[]>('GET', 'dbo/userGuides');
                flattenResourceHeaders(res.data, 'User Guides', undefined, results);
            },
        },
    ];

    // Per-grade sources. Each is filtered exactly the way its own page filters it.
    const gradeTasks = grades.flatMap((grade) => {
        const effectiveLanguage = filterLanguageSetting(grade, language);
        const gradeLabel = `Grade ${getDisplayGrade(grade)}`;

        return [
            {
                label: `${gradeLabel} lessons`,
                run: async () => {
                    const res = await apiRequest<UnitDTO[]>(
                        'GET',
                        `dbo/lessons?grade=${grade}&language=${effectiveLanguage}`
                    );
                    flattenUnits(
                        filterUnitsData(grade, res.data),
                        grade,
                        effectiveLanguage,
                        results
                    );
                },
            },
            {
                label: `${gradeLabel} assessments`,
                run: async () => {
                    const res = await apiRequest<ResourceHeaderDTO[]>(
                        'GET',
                        `dbo/assessments?grade=${grade}&language=${effectiveLanguage}`
                    );
                    flattenResourceHeaders(
                        filterAssessmentsData(res.data, grade, effectiveLanguage),
                        'Assessments',
                        grade,
                        results
                    );
                },
            },
            {
                label: `${gradeLabel} resources`,
                run: async () => {
                    const res = await apiRequest<ResourceHeaderDTO[]>(
                        'GET',
                        `dbo/resources?grade=${grade}&language=${effectiveLanguage}`
                    );
                    flattenResourceHeaders(
                        filterResourcesData(res.data, grade, effectiveLanguage),
                        'Resources',
                        grade,
                        results
                    );
                },
            },
        ];
    });

    // One source failing must not empty the whole index, so failures are collected
    // and reported rather than thrown.
    await mapWithConcurrency([...sharedTasks, ...gradeTasks], REQUEST_CONCURRENCY, async (task) => {
        try {
            await task.run();
        } catch {
            failedSources.push(task.label);
        }
    });

    flattenTextbooks(grades, language, results);

    return { results, failedSources };
}

// Cached per grade set + language. Teachers reopen the dialog constantly and the
// underlying content only changes on deploy, so rebuilding every time is wasteful.
const indexCache = new Map<string, Promise<SearchIndex>>();

export function getSearchIndex(grades: string[], language: LanguageOption): Promise<SearchIndex> {
    const cacheKey = `${language}|${grades.join(',')}`;
    const cached = indexCache.get(cacheKey);
    if (cached) return cached;

    const pending = buildIndex(grades, language).catch((error) => {
        // Never cache a hard failure -- the next open should retry.
        indexCache.delete(cacheKey);
        throw error;
    });
    indexCache.set(cacheKey, pending);
    return pending;
}

export function clearSearchIndexCache() {
    indexCache.clear();
}

export const MAX_SEARCH_RESULTS = 200;

// Groups results in a way a teacher reads as ordered: Unit 2 before Unit 10.
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Case-insensitive match across a file's name and the categories around it.
 * Every whitespace-separated word must appear somewhere, in any order, so
 * "unit 1 homework" finds the file named "Homework" that sits under Unit 1 --
 * and "grade 8 unit 1 homework" narrows it to one grade, because canonicalize()
 * binds each number to its label rather than letting a bare "8" match anything.
 */
export function searchResults(results: SearchResult[], query: string): SearchResult[] {
    const tokens = canonicalize(query).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];

    const joined = tokens.join(' ');
    const matches: { result: SearchResult; rank: number; position: number }[] = [];

    for (const result of results) {
        if (!tokens.every((token) => result.haystack.includes(token))) continue;

        const name = result.nameKey;
        const position = name.indexOf(tokens[0]);

        // Files whose own name carries the query outrank ones matched only by
        // their unit or category, so typing "homework" still leads with files
        // actually called Homework.
        let rank;
        if (name === joined) {
            rank = 0;
        } else if (position === 0) {
            rank = 1;
        } else if (tokens.every((token) => name.includes(token))) {
            rank = 2;
        } else {
            rank = 3;
        }

        matches.push({
            result,
            rank,
            position: position === -1 ? Number.MAX_SAFE_INTEGER : position,
        });
    }

    matches.sort(
        (a, b) =>
            a.rank - b.rank ||
            a.position - b.position ||
            // Within a rank, keep a grade's units and lessons in their natural order.
            collator.compare(a.result.grade ?? '', b.result.grade ?? '') ||
            collator.compare(a.result.trail.join(' '), b.result.trail.join(' ')) ||
            collator.compare(a.result.name, b.result.name) ||
            a.result.key.localeCompare(b.result.key)
    );

    return matches.slice(0, MAX_SEARCH_RESULTS).map((match) => match.result);
}
